import { ChakraProvider } from "@chakra-ui/react";
import { theme } from "./theme";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { DashboardLayout } from "./components/DashboardLayout";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import { PermissionRoute } from "./components/PermissionRoute";
import { ForbiddenPage } from "./pages/ForbiddenPage";
import { routes, ROUTE_PATHS } from "./config/routes";
import { ScrollToTop } from "./components/ScrollToTop";
import { PublicChatbotWidget } from "./components/PublicChatbotWidget";

const queryClient = new QueryClient();

const LegacyLoginRedirect = () => {
  const location = useLocation();

  return (
    <Navigate
      to={{
        pathname: ROUTE_PATHS.LOGIN,
        search: location.search,
        hash: location.hash,
      }}
      replace
    />
  );
};

function App() {
  // Separate routes by layout type
  const publicRoutes = routes.filter((r) => r.layout === "public");
  const dashboardRoutes = routes.filter((r) => r.layout === "dashboard");

  return (
    <ChakraProvider theme={theme}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Router>
            <ScrollToTop />
            <PublicChatbotWidget />
            <Routes>
              <Route path="/login" element={<LegacyLoginRedirect />} />

              {/* Public routes (including landing page at '/') */}
              {publicRoutes.map((route) => (
                <Route
                  key={route.path}
                  path={route.path}
                  element={
                    route.requiresAuth ? (
                      <ProtectedRoute>
                        <route.component />
                      </ProtectedRoute>
                    ) : (
                      <route.component />
                    )
                  }
                />
              ))}

              {/* Forbidden page */}
              <Route path="/forbidden" element={<ForbiddenPage />} />

              {/* Dashboard routes */}
              <Route
                path={ROUTE_PATHS.DASHBOARD.ROOT}
                element={
                  <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="home" replace />} />
                <Route
                  path="create-order"
                  element={<Navigate to="../home" replace />}
                />

                {dashboardRoutes.map((route) => {
                  const relativePath = route.path.replace(
                    ROUTE_PATHS.DASHBOARD.ROOT + "/",
                    "",
                  );

                  return (
                    <Route
                      key={route.path}
                      path={relativePath}
                      element={
                        <PermissionRoute permissions={route.permissions}>
                          <route.component />
                        </PermissionRoute>
                      }
                    />
                  );
                })}
              </Route>

              {/* Catch-all redirect */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Router>
        </AuthProvider>
      </QueryClientProvider>
    </ChakraProvider>
  );
}

export default App;
